// frontend_MainDashboard/src/components/Layout/ClientSwitcher.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useClient, AccessibleClient } from '../../contexts/ClientContext';

interface ClientSwitcherProps {
  className?: string;
  compact?: boolean;
}

const getRoleBadgeClass = (role: string) => {
  switch (role) {
    case 'owner':
      return 'bg-purple-100 text-purple-700';
    case 'admin':
      return 'bg-blue-100 text-blue-700';
    case 'agent':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getRoleLabel = (role: string) => {
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'admin':
      return 'Admin';
    case 'agent':
      return 'Agent';
    default:
      return role;
  }
};

const ClientSwitcher: React.FC<ClientSwitcherProps> = ({ className = '', compact = false }) => {
  const {
    selectedClientId,
    accessibleClients,
    isLoading,
    selectClient,
    refreshAccessibleClients,
  } = useClient();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Get current selected client
  const currentClient = accessibleClients.find((c) => c.id === selectedClientId);

  // Handle client selection
  const handleSelectClient = (client: AccessibleClient) => {
    selectClient(client.id);
    setIsOpen(false);
  };

  // Don't show switcher if only one client (or none)
  if (accessibleClients.length <= 1 && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="animate-pulse bg-surface-200 rounded-lg h-10 w-40"></div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border border-surface-300 
          hover:bg-surface-100 transition-colors ${compact ? 'text-sm' : ''}`}
        title="Switch workspace"
      >
        {/* Client Avatar */}
        <div className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} rounded-full bg-gradient-to-br from-primary-500 to-primary-700 
          flex items-center justify-center text-white font-medium overflow-hidden`}>
          {currentClient?.profilePictureUrl ? (
            <img
              src={currentClient.profilePictureUrl}
              alt={currentClient.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className={compact ? 'text-xs' : 'text-sm'}>
              {currentClient?.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          )}
        </div>

        {/* Client Name */}
        {!compact && (
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-medium text-surface-900 truncate max-w-[150px]">
              {currentClient?.name || 'Select Workspace'}
            </p>
            {currentClient?.role && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleBadgeClass(currentClient.role)}`}>
                {getRoleLabel(currentClient.role)}
              </span>
            )}
          </div>
        )}

        {/* Chevron */}
        <svg
          className={`w-4 h-4 text-surface-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-surface-200 
          z-50 max-h-80 overflow-y-auto">
          {/* Header */}
          <div className="px-4 py-3 border-b border-surface-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-surface-900">Switch Workspace</h3>
              <button
                onClick={() => refreshAccessibleClients()}
                className="text-xs text-primary-600 hover:text-primary-700"
                title="Refresh list"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Client List */}
          <div className="py-2">
            {accessibleClients.map((client) => (
              <button
                key={client.id}
                onClick={() => handleSelectClient(client)}
                className={`w-full px-4 py-3 flex items-center space-x-3 hover:bg-surface-50 transition-colors
                  ${selectedClientId === client.id ? 'bg-primary-50' : ''}`}
              >
                {/* Client Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 
                  flex items-center justify-center text-white font-medium flex-shrink-0 overflow-hidden">
                  {client.profilePictureUrl ? (
                    <img
                      src={client.profilePictureUrl}
                      alt={client.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{client.name?.charAt(0)?.toUpperCase() || '?'}</span>
                  )}
                </div>

                {/* Client Info */}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-surface-900 truncate">{client.name}</p>
                  <div className="flex items-center space-x-2">
                    {client.phoneNumber && (
                      <span className="text-xs text-surface-500 truncate">{client.phoneNumber}</span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleBadgeClass(client.role)}`}>
                      {getRoleLabel(client.role)}
                    </span>
                  </div>
                </div>

                {/* Selected Indicator */}
                {selectedClientId === client.id && (
                  <svg className="w-5 h-5 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>

          {/* No clients message */}
          {accessibleClients.length === 0 && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-surface-500">No workspaces available</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSwitcher;
